import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CustomInputTextField from 'components/shared/custom_input_textfield';

export const frequencyOptions = ['Diária', 'Semanal', 'Quinzenal', 'Mensal', 'Outro'];
export const publicOptions = [
  'Consumidores locais',
  'Turistas',
];

export interface NonPermanentMarket {
  name: string;
  address: string;
  neighborhood: string;
  zone: string;
  frequency: string;
  otherFrequency: string;
  predominantPublic: string;
  numFamilyFarmProducers: number;
  numWomenFamilyFarmProducers: number;
  numFamilyFarmResellers: number;
  numWomenFamilyFarmResellers: number;
  numHandicraftSellers: number;
  numWomenHandicraftSellers: number;
  numIndustrialSellers: number;
  totalMarketValue: number;
  annualMarketValue: number;
  totalFamilyFarmingValue: number;
  equipments: string[];
  otherEquipment: string;
  [key: string]: any;
}

export interface Step4SimData {
  referenceYear: number;
  nonPermanentMarkets: NonPermanentMarket[];
}

interface Step4Props {
  onNext: () => void;
  onBack: () => void;
  onChange: (data: Step4SimData) => void;
  data: Step4SimData;
  disabled?: boolean;
  referenceYear: number;
  /** Quantidade de feiras livres declarada na Etapa 2: 0 = não exige cadastros; >0 = exige exatamente essa quantidade */
  requiredCount: number;
}

export const emptyMarket: NonPermanentMarket = {
  name: '',
  address: '',
  neighborhood: '',
  zone: '',
  frequency: '',
  otherFrequency: '',
  predominantPublic: '',
  numFamilyFarmProducers: 0,
  numWomenFamilyFarmProducers: 0,
  numFamilyFarmResellers: 0,
  numWomenFamilyFarmResellers: 0,
  numHandicraftSellers: 0,
  numWomenHandicraftSellers: 0,
  numIndustrialSellers: 0,
  totalMarketValue: 0,
  annualMarketValue: 0,
  totalFamilyFarmingValue: 0,
  equipments: [],
  otherEquipment: '',
};

export const formatMonetaryInput = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (!cleaned) return '';
  const num = parseFloat(cleaned) / 100;
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const parseMonetaryValue = (formattedValue: string): number => {
  const parsable = formattedValue.replace(/\./g, '').replace(',', '.');
  const value = parseFloat(parsable);
  return isNaN(value) ? 0 : Math.round(value * 100);
};

export const formatCurrency = (value: number | undefined | null) =>
  typeof value === 'number' && !isNaN(value)
    ? (value / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : 'Não informado';

export const validateMarket = (market: NonPermanentMarket) => {
  const newErrors: { [key: string]: string } = {};

  const requiredFields = [
    { key: 'name', label: 'Campo obrigatório' },
    { key: 'address', label: 'Campo obrigatório' },
    { key: 'neighborhood', label: 'Campo obrigatório' },
    { key: 'zone', label: 'Campo obrigatório' },
    { key: 'frequency', label: 'Campo obrigatório' },
    { key: 'predominantPublic', label: 'Campo obrigatório' },
  ];

  requiredFields.forEach(({ key, label }) => {
    if (!market[key]) newErrors[key] = label;
  });

  if (market.frequency === 'Outro' && !market.otherFrequency) {
    newErrors.otherFrequency = 'Campo obrigatório';
  }

  const numberFields = [
    { key: 'numFamilyFarmProducers', label: 'Obrigatório' },
    { key: 'numWomenFamilyFarmProducers', label: 'Obrigatório' },
    { key: 'numFamilyFarmResellers', label: 'Obrigatório' },
    { key: 'numWomenFamilyFarmResellers', label: 'Obrigatório' },
    { key: 'numHandicraftSellers', label: 'Obrigatório' },
    { key: 'numWomenHandicraftSellers', label: 'Obrigatório' },
    { key: 'numIndustrialSellers', label: 'Obrigatório' },
  ];

  numberFields.forEach(({ key, label }) => {
    if (market[key] < 0) newErrors[key] = label;
  });

  if (!market.totalMarketValue || market.totalMarketValue < 0) {
    newErrors.totalMarketValue = 'Obrigatório';
  }

  if (!market.totalFamilyFarmingValue || market.totalFamilyFarmingValue < 0) {
    newErrors.totalFamilyFarmingValue = 'Obrigatório';
  }

  return newErrors;
};

const Step4: React.FC<Step4Props> = ({
  onNext,
  onBack,
  onChange,
  data,
  disabled = false,
  referenceYear,
  requiredCount = 0,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const count = data.nonPermanentMarkets?.length ?? 0;
  const addButtonDisabled =
    disabled || requiredCount === 0 || count >= requiredCount;
  const canContinue =
    requiredCount === 0 ? true : count === requiredCount;
  const [openDialog, setOpenDialog] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [market, setMarket] = useState<NonPermanentMarket>(emptyMarket);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Estados locais para preservar a digitação dos valores monetários
  const [annualMarketValueInput, setAnnualMarketValueInput] = useState('0.00');
  const [totalFamilyFarmingValueInput, setTotalFamilyFarmingValueInput] = useState('0.00');
  const [totalMarketValueInput, setTotalMarketValueInput] = useState('0.00');

  // Atualiza o estado global sempre que a lista de feiras mudar
  const handleMarketsChange = (markets: NonPermanentMarket[]) => {
    onChange({
      referenceYear,
      nonPermanentMarkets: markets,
    });
  };

  const handleOpenDialog = (editMarket?: NonPermanentMarket, index?: number) => {
    const marketData = editMarket
      ? { ...emptyMarket, ...editMarket, equipments: editMarket.equipments ?? [] }
      : emptyMarket;
    setMarket(marketData);

    // Formata os valores monetários para exibição
    setAnnualMarketValueInput(
      (marketData.annualMarketValue / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
      })
    );
    setTotalFamilyFarmingValueInput(
      (marketData.totalFamilyFarmingValue / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
      })
    );
    setTotalMarketValueInput(
      (marketData.totalMarketValue / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
      })
    );

    setErrors({});
    setEditIndex(typeof index === 'number' ? index : null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setErrors({});
    setEditIndex(null);
  };

  const handleMarketChange = (field: keyof NonPermanentMarket, value: any) => {
    setMarket((prev) => ({
      ...prev,
      [field]: value,
    }));
  };


  const handleTotalFamilyFarmingValueChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formatted = formatMonetaryInput(e.target.value);
    setTotalFamilyFarmingValueInput(formatted);
    const centavos = parseMonetaryValue(formatted);
    handleMarketChange('totalFamilyFarmingValue', centavos);
  };

  const handleTotalMarketValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatMonetaryInput(e.target.value);
    setTotalMarketValueInput(formatted);
    const centavos = parseMonetaryValue(formatted);
    handleMarketChange('totalMarketValue', centavos);
  };




  const handleSaveMarket = () => {
    const validation = validateMarket(market);
    setErrors(validation);
    if (Object.keys(validation).length === 0) {
      const updatedMarkets = [...(data.nonPermanentMarkets || [])];
      if (editIndex !== null) {
        updatedMarkets[editIndex] = market;
      } else {
        updatedMarkets.push(market);
      }
      handleMarketsChange(updatedMarkets);
      setOpenDialog(false);
      setEditIndex(null);
    }
  };

  const handleRemoveMarket = (index: number) => {
    const updated = [...(data.nonPermanentMarkets || [])];
    updated.splice(index, 1);
    handleMarketsChange(updated);
  };


  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Feiras Livres cadastradas</Typography>
          <Button variant="contained" onClick={() => handleOpenDialog()} disabled={addButtonDisabled}>
            Adicionar Feira Livre
          </Button>
        </Box>
        {(!data.nonPermanentMarkets || data.nonPermanentMarkets.length === 0) && (
          <Typography color="text.secondary">Nenhuma feira cadastrada.</Typography>
        )}
        <Box>
          {data.nonPermanentMarkets && data.nonPermanentMarkets.map((mkt, idx) => (
            <Paper key={idx} sx={{ p: 2, mb: 2, position: 'relative' }}>
              <IconButton
                aria-label="Editar"
                onClick={() => handleOpenDialog(mkt, idx)}
                sx={{ position: 'absolute', top: 8, right: 48 }}
                disabled={disabled}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                aria-label="Remover"
                onClick={() => handleRemoveMarket(idx)}
                sx={{ position: 'absolute', top: 8, right: 8 }}
                disabled={disabled}
              >
                <DeleteIcon />
              </IconButton>
              <Typography variant="subtitle2"><b>{mkt.name}</b> - {mkt.neighborhood} ({mkt.zone})</Typography>
              <Typography variant="body2">Endereço: {mkt.address}</Typography>
              <Typography variant="body2">
                Frequência: {mkt.frequency}{mkt.frequency === 'Outro' && mkt.otherFrequency ? ` (${mkt.otherFrequency})` : ''}
              </Typography>
              <Typography variant="body2">Público predominante: {mkt.predominantPublic}</Typography>
              <Typography variant="body2">Comerciantes/produtores da Agricultura Familiar: {mkt.numFamilyFarmProducers}</Typography>
              <Typography variant="body2">Mulheres comerciantes/produtoras da Agricultura Familiar: {mkt.numWomenFamilyFarmProducers}</Typography>
              <Typography variant="body2">Comerciantes da Agricultura Familiar (Revendedores): {mkt.numFamilyFarmResellers}</Typography>
              <Typography variant="body2">Mulheres comerciantes (revendedoras) da Agricultura Familiar: {mkt.numWomenFamilyFarmResellers}</Typography>
              <Typography variant="body2">Comerciantes de artesanato: {mkt.numHandicraftSellers}</Typography>
              <Typography variant="body2">Mulheres comerciantes de artesanato: {mkt.numWomenHandicraftSellers}</Typography>
              <Typography variant="body2">Comerciantes de produtos beneficiados/industrializados: {mkt.numIndustrialSellers}</Typography>
              <Typography variant="body2">Valor total comercializado na feira: {formatCurrency(mkt.totalMarketValue)}</Typography>
              {/*<Typography variant="body2">Valor comercializado nas feiras do município anualmente: {formatCurrency(mkt.annualMarketValue)}</Typography>*/}
              <Typography variant="body2">Valor total comercializado pela Agricultura Familiar: {formatCurrency(mkt.totalFamilyFarmingValue)}</Typography>
            </Paper>
          ))}
        </Box>


      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editIndex !== null ? 'Editar Feira Livre' : 'Adicionar Feira Livre'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nome da feira"
                value={market.name}
                required
                onChange={e => handleMarketChange('name', e.target.value.toUpperCase())}
                helperText={errors.name}
                error={!!errors.name}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Endereço da feira"
                value={market.address}
                fullWidth
                onChange={e => handleMarketChange('address', e.target.value.toUpperCase())}
                error={!!errors.address}
                helperText={errors.address}

                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Bairro/localidade"
                value={market.neighborhood}
                onChange={e => handleMarketChange('neighborhood', e.target.value.toUpperCase())}
                required
                error={!!errors.neighborhood}
                helperText={errors.neighborhood}
                fullWidth

              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Zona"
                value={market.zone}
                onChange={e => handleMarketChange('zone', e.target.value)}
                error={!!errors.zone}
                helperText={errors.zone}
                fullWidth
                required
              >
                <MenuItem value="Rural">Rural</MenuItem>
                <MenuItem value="Urbana">Urbana</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={12}>
              <Typography sx={{ mb: 1 }}>A feira ocorre com frequência:</Typography>
              <RadioGroup
                row
                value={market.frequency}
                onChange={e => handleMarketChange('frequency', e.target.value)}
              >
                {frequencyOptions.map(option => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio />}
                    label={option}
                  />
                ))}
              </RadioGroup>
              {errors.frequency && <Typography color="error">{errors.frequency}</Typography>}
              {market.frequency === 'Outro' && (
                <TextField
                  label="Especifique a frequência"
                  value={market.otherFrequency}
                  onChange={e => handleMarketChange('otherFrequency', e.target.value)}
                  error={!!errors.otherFrequency}
                  helperText={errors.otherFrequency}
                  fullWidth
                  margin="dense"
                  required
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Qual o público predominante?"
                value={market.predominantPublic}
                onChange={e => handleMarketChange('predominantPublic', e.target.value)}
                error={!!errors.predominantPublic}
                fullWidth
                helperText={errors.predominantPublic}
                required
              >
                {publicOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* Campos numéricos e monetários */}
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Comerciantes/produtores da Agricultura Familiar"
                type="number"
                inputProps={{ min: 0 }}
                value={market.numFamilyFarmProducers}
                onChange={e => handleMarketChange('numFamilyFarmProducers', Number(e.target.value))}
                error={errors}
                helperText={errors}
                fullWidth
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Mulheres comerciantes/produtoras da Agricultura Familiar"
                type="number"
                inputProps={{ min: 0 }}
                value={market.numWomenFamilyFarmProducers}
                onChange={e => handleMarketChange('numWomenFamilyFarmProducers', Number(e.target.value))}
                error={errors}
                helperText={errors}
                fullWidth
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Comerciantes da Agricultura Familiar (Revendedores)"
                type="number"
                inputProps={{ min: 0 }}
                value={market.numFamilyFarmResellers}
                onChange={e => handleMarketChange('numFamilyFarmResellers', Number(e.target.value))}
                error={errors}
                helperText={errors}
                margin="dense"
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Mulheres comerciantes (revendedoras) da Agricultura Familiar"
                type="number"
                inputProps={{ min: 0 }}
                value={market.numWomenFamilyFarmResellers}
                onChange={e => handleMarketChange('numWomenFamilyFarmResellers', Number(e.target.value))}
                error={errors}
                helperText={errors}
                margin="dense"
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Comerciantes de artesanato"
                type="number"
                inputProps={{ min: 0 }}
                value={market.numHandicraftSellers}
                onChange={e => handleMarketChange('numHandicraftSellers', Number(e.target.value))}
                error={errors}
                helperText={errors}
                margin="dense"
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Mulheres comerciantes de artesanato"
                type="number"
                inputProps={{ min: 0 }}
                value={market.numWomenHandicraftSellers}
                onChange={e => handleMarketChange('numWomenHandicraftSellers', Number(e.target.value))}
                error={errors}
                helperText={errors}
                margin="dense"
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomInputTextField
                label="Quantos são comerciantes de produtos beneficiados/industrializados?"
                type="number"
                inputProps={{ min: 0 }}
                value={market.numIndustrialSellers}
                onChange={e => handleMarketChange('numIndustrialSellers', Number(e.target.value))}
                error={errors}
                helperText={errors}
                margin="dense"
                fullWidth
                required
              />
            </Grid>
            {/*<Grid item xs={12} md={6}>
                            <TextField
                                label="Valor comercializado nas feiras do município anualmente (R$)"
                                value={annualMarketValueInput}
                                onChange={handleAnnualMarketValueChange}
                                error={!!errors.annualMarketValue}
                                helperText={errors.annualMarketValue}
                                fullWidth
                                required
                                inputProps={{ inputMode: 'decimal' }}
                                InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>R$</span> }}
                                placeholder="0.00"
                            />
                        </Grid>*/}
            <Grid item xs={12} md={6}>
              <TextField
                label="Valor total comercializado pela Agricultura Familiar (R$)"
                value={totalFamilyFarmingValueInput}
                onChange={handleTotalFamilyFarmingValueChange}
                error={!!errors.totalFamilyFarmingValue}
                helperText={errors.totalFamilyFarmingValue}
                fullWidth
                required
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{
                  startAdornment: <span style={{ marginRight: 4 }}>R$</span>,
                }}
                placeholder="0,00"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Valor total comercializado na feira anualmente (R$)"
                value={totalMarketValueInput}
                onChange={handleTotalMarketValueChange}
                error={!!errors.totalMarketValue}
                helperText={errors.totalMarketValue}
                fullWidth
                required
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{
                  startAdornment: <span style={{ marginRight: 4 }}>R$</span>,
                }}
                placeholder="0,00"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSaveMarket} variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="outlined" onClick={onBack} disabled={disabled}>
          Voltar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (disabled) return;
            if (!canContinue) {
              enqueueSnackbar(
                'Não é possível continuar. Adicione as feiras livres declaradas na Etapa 2.',
                { variant: 'warning' },
              );
              return;
            }
            onNext();
          }}
          size="large"
          disabled={disabled}
          sx={
            !canContinue && !disabled
              ? { opacity: 0.7, cursor: 'not-allowed' }
              : undefined
          }
        >
          Salvar e continuar
        </Button>
      </Box>
    </Box>
  );
};

export default Step4;
